import "./App.css";
import { useState, lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Landing from "./pages/Landing";
import AuthRoute from "./pages/AuthRoute";
import ChatPage from "./pages/ChatPage";
import OnboardingWizard from "./components/OnboardingWizard";
import { AppContextProvider, useAppContext } from "./context/AppContext";
import AppProviders from "./providers/AppProviders";
import RuntimeProgressOverlay from "./components/common/RuntimeProgressOverlay";
import ImportChoiceModal from "./components/common/ImportChoiceModal";
import ImportReviewScreen from "./components/common/ImportReviewScreen";
import { getPendingFile, clearPendingFile } from "./utils/dbHelper";

// PrintPage is lazy-loaded so it never bloats the main bundle.
// It is intentionally mounted OUTSIDE AppContextProvider — zero editor state.
const PrintPage = lazy(() => import('./pages/PrintPage'));

// VaultPublicPage is also mounted outside AppContextProvider —
// it is a public recruiter-facing page, no auth, no editor state.
const VaultPublicPage = lazy(() => import('./pages/VaultPublicPage'));

const About = lazy(() => import('./pages/About'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));


function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken, user, theme, authInitializing } = useAppContext();

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleOnboardingComplete = (profile) => {
    setShowOnboarding(false);
    // Apply recommended template from onboarding
    if (profile.recommendedTemplate && !profile.skipped) {
      localStorage.setItem('rz_template', profile.recommendedTemplate);
    }
  };

  if (authInitializing) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
        <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
        <div>Verifying session...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          authToken ? (
            <Navigate to="/app/resume/master/chat" replace />
          ) : (
            <Landing
              onSignIn={() => navigate('/login')}
              onGetStarted={() => navigate('/login')}
            />
          )
        }
      />
      <Route
        path="/login"
        element={
          authToken ? (
            (() => {
              const intended = localStorage.getItem('mmr_redirect_after_login') || '/app/resume/master/chat';
              localStorage.removeItem('mmr_redirect_after_login');
              return <Navigate to={intended} replace />;
            })()
          ) : (
            <AuthRoute />
          )
        }
      />
      <Route
        path="/app/*"
        element={
          !authToken ? (
            <Navigate to="/login" replace state={{ from: location }} />
          ) : (
            <>
              {showOnboarding && (
                <OnboardingWizard
                  onComplete={handleOnboardingComplete}
                  theme={theme}
                  userName={user?.name || ''}
                />
              )}
              <ChatPage />
            </>
          )
        }
      />
      <Route
        path="/about"
        element={
          <Suspense fallback={
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
              <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <About />
          </Suspense>
        }
      />
      <Route
        path="/terms"
        element={
          <Suspense fallback={
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
              <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path="/privacy"
        element={
          <Suspense fallback={
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
              <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path="/help"
        element={
          <Suspense fallback={
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
              <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <HelpPage />
          </Suspense>
        }
      />
      <Route
        path="/plans"
        element={
          <Suspense fallback={
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#0B1220] text-[#FAFAF8] font-sans text-xs gap-3">
              <div className="w-6 h-6 border-2 border-[#FCA311] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <PlansPage />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppGlobalOverlays() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeProgress, setActiveProgress,
    showImportModal, setShowImportModal,
    handleImportModalConfirm,
    importReviewData,
    importMode,
    handleImportAccept,
    handleImportEdit,
    handleImportDiscard,
    authToken,
    handleResumeUpload,
    resumeFileInputRef,
    hasActiveConversation: hasConversationMessages,
  } = useAppContext();

  // "Continue in current conversation" only makes sense while the user is
  // actually inside a conversation view right now. session.conversation
  // persists across navigation (it's global SPA state), so checking just
  // "does it have messages" stays true even after going back to Dashboard —
  // route-gate it so leaving the chat view hides the option again.
  const isInsideConversationView = /\/app\/resume\/[^/]+\/(chat|editor)/.test(location.pathname)
    || location.pathname.startsWith('/app/cover-letter/');
  const hasActiveConversation = isInsideConversationView && hasConversationMessages;

  // Handle auto-import of pending resume file from landing page after login
  useEffect(() => {
    if (sessionStorage.getItem('pending_import') === 'true') {
      sessionStorage.removeItem('pending_import');
      getPendingFile().then((file) => {
        if (file) {
          handleResumeUpload(file);
          clearPendingFile();
        }
      });
    }
  }, [handleResumeUpload]);

  if (!authToken) return null;

  return (
    <>
      {/* Global progress overlay — visible from any route */}
      {activeProgress && (
        <RuntimeProgressOverlay progress={activeProgress} />
      )}

      {/*
        Hidden file input — MUST live here (global overlay layer) so that
        resumeFileInputRef.current is always populated when ImportChoiceModal
        calls handleImportModalConfirm. Previously this input only existed
        inside ChatPageContent, so the ref was null when the modal fired
        from any route that hadn't fully mounted ChatPage yet.
      */}
      <input
        type="file"
        ref={resumeFileInputRef}
        onChange={handleResumeUpload}
        accept=".pdf,.docx"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/* Import choice modal — shown before file dialog opens */}
      <ImportChoiceModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onConfirm={handleImportModalConfirm}
        hasActiveConversation={hasActiveConversation}
      />

      {/* Import review screen — shown after parse, before committing data */}
      {importReviewData && (
        <ImportReviewScreen
          parsed={importReviewData}
          onAccept={async () => {
            // CRITICAL: await handleImportAccept before navigating.
            // handleImportAccept is async — it calls loadConversation which
            // creates/loads the correct session on the backend. If we navigate
            // first (race condition), the route re-renders and triggers effects
            // that reload the OLD session, overwriting the new one. Awaiting
            // ensures the new session is fully in state before any re-render.
            // Returns the target document ID — 'master' unless this import
            // came from "Create New", in which case it's the freshly created doc.
            const targetDocId = await handleImportAccept(importReviewData);
            const targetPath = (targetDocId && targetDocId !== 'master')
              ? `/app/resume/${targetDocId}/chat`
              : '/app/resume/master/chat';
            // Only navigate if we're NOT already on that route.
            // Navigating to the same path in React Router v6 creates a new
            // history entry, triggers location-dependent re-renders, and risks
            // overwriting the freshly-loaded session with the old one.
            if (location.pathname !== targetPath) {
              navigate(targetPath);
            }
          }}
          onEdit={() => {
            // BUG-S07 fix: handleImportEdit emitted a dead AppEvents event.
            // Now we call it and then navigate directly to the editor.
            handleImportEdit(importReviewData);
            navigate('/app/resume/master/editor');
          }}
          onDiscard={handleImportDiscard}
        />
      )}
    </>
  );
}

/**
 * AppShell — top-level route switcher.
 *
 * Print routes (/print/*) bypass AppContextProvider entirely so that
 * PrintPage loads with zero editor state, zero workspace, zero conversation.
 * This is a hard architectural requirement: the PDF renderer must be
 * independent of the application shell.
 *
 * All other routes enter the full AppContextProvider as before.
 */
function AppShell() {
  const location = useLocation();

  if (location.pathname.startsWith('/print/')) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/print/document/:id" element={<PrintPage />} />
          {/* Future: /print/cover-letter/:id, /print/portfolio/:id */}
        </Routes>
      </Suspense>
    );
  }

  // Career public profile page — no auth, no editor state
  if (location.pathname.startsWith('/career/')) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/career/:slug" element={<VaultPublicPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AppContextProvider>
      <AppRoutes />
      <AppGlobalOverlays />
    </AppContextProvider>
  );
}

function App() {
  return (
    <AppProviders>
      <AppShell />
    </AppProviders>
  );
}

export default App;
